/**
 * Receives a `featit-return` link once the OS hands it to this app (I-1078, ADR-160: "A return path
 * for OAuth sign-in and Stripe").
 *
 * The desktop app registers the scheme (`tauri-plugin-deep-link`, `tauri.conf.json`'s
 * `plugins.deep-link.desktop.schemes`). The Rust side queues each link the OS hands over, brings a
 * window forward, and tells that window `desktop-return://arrived` (`desktop_return_link.rs`, ADR-160
 * AC-25, AC-26); this window then takes the waiting links with `take_desktop_return_links`, which
 * drains the queue. It takes once at start too — the cold-launch case, or a link that arrived while
 * this page was loading.
 *
 * ⛔ Never the plugin's own `get_current`: it keeps the last link for the life of the process, so a
 * page that asks it on every start replays an old return after any later reload.
 *
 * This module only turns that URL into an app-relative path and query — `desktopReturnPath` refuses
 * anything that is not one of this app's own `featit-return` links (shared/src/desktop/return-link.ts,
 * AC-10) — and hands it to whatever the caller does with a path: a client-side route change, the
 * same one `LoginPage`, `ProfilePage` and the billing settings screen already read a *web* return
 * from. No new screen, no new parsing of `oauthCode`/`oauthError`/`oauthLinked`/`namespace`/
 * `checkout` — those are read exactly where the web case already reads them.
 */
import { desktopReturnPath } from '@featit/shared/desktop/return-link';
import { isDesktopRuntime } from './desktopDetection';

/** What the Rust side tells the window a return goes to; the link itself is taken, not sent. */
export const DESKTOP_RETURN_ARRIVED_EVENT = 'desktop-return://arrived';

/** Stops the subscription this module started. A no-op outside the desktop runtime. */
export type StopListeningType = () => void;

/** The path(s) carried by whatever `take_desktop_return_links` answered with. */
function pathsFrom(urls: unknown): string[] {
  if (!Array.isArray(urls)) return [];
  return urls
    .filter((entry): entry is string => typeof entry === 'string')
    .map(desktopReturnPath)
    .filter((path): path is string => path !== null);
}

/**
 * Starts listening for a `featit-return` link, and checks for one that already arrived before this
 * ran (the app was launched by it). `onPath` is called with the app-relative path and query for
 * each one found — potentially more than once, if the person starts a second round trip before
 * this app quits.
 *
 * A no-op outside the desktop runtime: a browser tab is never handed one of these.
 */
export async function listenForDesktopReturnLinks(
  onPath: (path: string) => void,
): Promise<StopListeningType> {
  if (!isDesktopRuntime()) return () => {};

  const [{ listen }, { invoke }] = await Promise.all([
    import('@tauri-apps/api/event'),
    import('@tauri-apps/api/core'),
  ]);

  // Best-effort: a build or a fixture without the command answers with nothing to route, not an
  // error worth surfacing.
  const take = async () => {
    try {
      pathsFrom(await invoke('take_desktop_return_links')).forEach((path) => onPath(path));
    } catch {
      // Nothing waiting, or the command is not there. Either way, nothing to route.
    }
  };

  // Subscribe before the first take, so a link arriving in between is told rather than missed.
  // Best-effort as well: `listen` needs more of the native bridge
  // (`__TAURI_INTERNALS__.transformCallback`) than `isDesktopRuntime()` itself checks for, so a
  // desktop e2e fixture that fakes only `invoke` — every one of them, today — must not see this
  // throw. A real desktop build always has the whole bridge.
  let stop: StopListeningType = () => {};
  try {
    stop = await listen<unknown>(DESKTOP_RETURN_ARRIVED_EVENT, () => {
      void take();
    });
  } catch {
    // No subscription: the take at start below is all this page gets.
  }
  await take();
  return stop;
}
